import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import CtaBand from "@/components/CtaBand";
import BookCarousel from "@/components/BookCarousel";
import IconOrbit from "@/components/IconOrbit";
import { Reveal } from "@/components/Reveal";
import api from "@/lib/api";

export default function ArticleDetail() {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    setArticle(null);
    setMissing(false);
    api
      .get(`/articles/${slug}`)
      .then((r) => {
        setArticle(r.data);
        document.title = `${r.data.title} — Advanced Risk Management Solutions`;
      })
      .catch(() => setMissing(true));
  }, [slug]);

  if (missing) {
    return (
      <div className="container-arms py-32 text-center" data-testid="article-not-found">
        <h1 className="text-2xl sm:text-3xl font-bold text-navy">Article not found</h1>
        <Link to="/insights" className="mt-6 inline-flex items-center gap-2 font-semibold text-teal hover:text-navy">
          <ArrowLeft size={16} /> Back to insights
        </Link>
      </div>
    );
  }

  if (!article) {
    return <div className="container-arms py-32 text-slate-500" data-testid="article-loading">Loading article…</div>;
  }

  const paragraphs = (article.content || "").split(/\n\s*\n/).filter(Boolean);
  const isBooksArticle = article.slug === "what-influenced-the-arms-ltd-approach";
  const isAssessArticle = article.slug === "understanding-a-culture-is-simple-if-you-ask-the-right-questions-of-the-right-people";
  const isComplacencyArticle = article.slug === "how-complacent-attitudes-form-and-why";
  const isCultureArticle = article.slug === "culture-is-the-collective-behaviour-of-everyone-in-the-organisation";
  const ATTITUDE_RING = [1, 2, 3, 4, 5, 6].map((n) => `/assets/icons/attitude-a${n}.png`);
  const ATTITUDE_LABELS = [
    "Safety can get in the way",
    "I know how to cut corners safely",
    "My experience means I always know the risks",
    "Mistakes are the main reason things are done unsafely",
    "Even if I break the rules it's still safe",
    "Things just go wrong for NO reason!",
  ];
  const DIMENSION_RING = [
    "/assets/icons/safety-leadership.png",
    "/assets/icons/competence.png",
    "/assets/icons/communications.png",
    "/assets/icons/commitment.png",
    "/assets/icons/safety-systems.png",
    "/assets/icons/measurement-feedback.png",
  ];
  const DIMENSION_LABELS = [
    "Safety Leadership",
    "Competence",
    "Communications",
    "Commitment",
    "Safety Systems",
    "Measurement & Feedback",
  ];
  const ASSESS_ICONS = {
    "Safety Leadership": "/assets/icons/safety-leadership.png",
    "Competence": "/assets/icons/competence.png",
    "Communications": "/assets/icons/communications.png",
    "Commitment": "/assets/icons/commitment.png",
    "Safety Systems": "/assets/icons/safety-systems.png",
    "Measurement & Feedback": "/assets/icons/measurement-feedback.png",
  };
  const ATTITUDE_ICONS = {
    "1": "/assets/icons/attitude-a1.png",
    "2": "/assets/icons/attitude-a2.png",
    "3": "/assets/icons/attitude-a3.png",
    "4": "/assets/icons/attitude-a4.png",
    "5": "/assets/icons/attitude-a5.png",
    "6": "/assets/icons/attitude-a6.png",
  };
  const BOOK_COVERS = [
    { match: "Bringing Out the Best in People", src: "/assets/books/bringing-out-the-best.jpg" },
    { match: "Performance Management", src: "/assets/books/performance-management.jpg" },
    { match: "The Tipping Point", src: "/assets/books/tipping-point.jpg" },
    { match: "The Human Contribution", src: "/assets/books/human-contribution.jpg" },
    { match: "Games People Play", src: "/assets/books/games-people-play.jpg" },
    { match: "Just Culture", src: "/assets/books/just-culture.jpg" },
  ];

  return (
    <div data-testid="article-detail-page">
      <article className="bg-white">
        <header className="relative overflow-hidden bg-slate-50">
          <div className="absolute -right-32 -top-32 h-96 w-96 rounded-full bg-teal-light blur-3xl opacity-70" aria-hidden="true" />
          <div className="container-arms relative max-w-4xl py-20 lg:py-28">
            <Reveal>
              <Link to="/insights" data-testid="article-back-link" className="mb-8 inline-flex items-center gap-2 text-sm font-semibold text-teal hover:text-navy transition-colors">
                <ArrowLeft size={16} /> All insights
              </Link>
              <p className="eyebrow mb-4">{article.category}</p>
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight leading-[1.1] text-navy">{article.title}</h1>
              <p className="mt-6 font-mono text-xs uppercase tracking-[0.15em] text-slate-500">
                {article.author} · {article.published_at ? new Date(article.published_at).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" }) : ""}
              </p>
            </Reveal>
          </div>
        </header>
        {article.cover_image && article.slug === "what-influenced-the-arms-ltd-approach" && (
          <div className="container-arms max-w-4xl -mt-6">
            <BookCarousel />
          </div>
        )}
        {article.cover_image && article.slug !== "what-influenced-the-arms-ltd-approach" && (
          <div className="container-arms max-w-4xl">
            <img src={article.cover_image} alt="" className="-mt-10 aspect-[16/8] w-full rounded-3xl object-cover img-duotone shadow-2xl" />
          </div>
        )}
        <div className="container-arms max-w-3xl py-16 lg:py-20" data-testid="article-body">
          <p className="text-lg sm:text-xl font-medium leading-relaxed text-navy">{article.excerpt}</p>
          <div className="mt-8 space-y-6">
            {paragraphs.map((p, i) => {
              if (isCultureArticle && p.trim().startsWith("1) a change in attitudes")) {
                return (
                  <div key={i} className="space-y-3" data-testid="culture-change-rings">
                    <p className="text-base sm:text-lg font-bold text-navy leading-relaxed whitespace-pre-line">1) a change in attitudes towards risk</p>
                    <IconOrbit icons={ATTITUDE_RING} labels={ATTITUDE_LABELS} testId="attitudes-ring" className="relative mx-auto aspect-square w-[360px] sm:w-[440px]" />
                    <p className="text-base sm:text-lg font-bold text-navy leading-relaxed whitespace-pre-line">2) changes in behaviour</p>
                    <IconOrbit icons={DIMENSION_RING} labels={DIMENSION_LABELS} labelClass="text-blue-700" testId="dimensions-ring" className="relative mx-auto aspect-square w-[340px] sm:w-[420px]" />
                  </div>
                );
              }

              const imgMatch = p.match(/^\[img:(.+?)\]$/);
              if (imgMatch) {
                return (
                  <div key={i} className="my-4">
                    <img src={imgMatch[1]} alt="Article diagram" loading="lazy" className="w-full rounded-2xl border border-slate-200" data-testid={`article-inline-image-${i}`} />
                  </div>
                );
              }
              const lead = p.match(/^(Phase \d+\s*[–—-][^:]*:)\s*(.*)$/s);
              if (lead) {
                return (
                  <p key={i} className="text-base sm:text-lg text-slate-600 leading-relaxed whitespace-pre-line">
                    <span className="font-bold text-teal">{lead[1]}</span> {lead[2]}
                  </p>
                );
              }
              const dimIcon = isAssessArticle ? ASSESS_ICONS[p.trim()] : undefined;
              if (dimIcon) {
                return (
                  <p key={i} className="flex items-center gap-4 pt-4" data-testid={`assess-heading-${p.trim().toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                    <img src={dimIcon} alt="" aria-hidden="true" className="h-11 w-11 shrink-0 object-contain" />
                    <span className="text-lg sm:text-xl font-bold text-navy">{p.trim()}</span>
                  </p>
                );
              }
              return (
                <p key={i} className="text-base sm:text-lg text-slate-600 leading-relaxed whitespace-pre-line">
                  {p.split("\n").map((line, j) => {
                    const isBookTitle = isBooksArticle && /^.{3,60} — .{2,40}$/.test(line.trim());
                    const cover = isBookTitle ? BOOK_COVERS.find((b) => line.trim().startsWith(b.match)) : null;
                    const bulletText = isAssessArticle && line.trim().startsWith("• ") ? line.trim().slice(2) : null;
                    const assessIcon = bulletText ? ASSESS_ICONS[bulletText] : undefined;
                    if (bulletText && assessIcon) {
                      return (
                        <span key={j} className="flex items-center gap-4 py-1.5" data-testid={`assess-icon-${bulletText.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                          <img src={assessIcon} alt="" aria-hidden="true" className="h-10 w-10 shrink-0 object-contain" />
                          <span className="font-semibold text-navy">{bulletText}</span>
                        </span>
                      );
                    }
                    const attitude = (isAssessArticle || isComplacencyArticle) ? line.trim().match(/^A([1-6])\.\s/) : null;
                    if (attitude) {
                      const isShortStatement = !line.includes(" – ");
                      return (
                        <span key={j} className="flex items-center gap-4 py-2" data-testid={`attitude-icon-a${attitude[1]}`}>
                          <img src={ATTITUDE_ICONS[attitude[1]]} alt="" aria-hidden="true" className="h-12 w-12 shrink-0 object-contain" />
                          <span className={"font-bold text-teal" + (isShortStatement ? " lg:whitespace-nowrap" : "")}>{line.trim()}</span>
                        </span>
                      );
                    }
                    const isAttitudeQuestion = isAssessArticle && line.trim().endsWith("?") && !/^Key assessment:/.test(line.trim());
                    if (isAttitudeQuestion) {
                      return (
                        <span key={j} className="flex items-start gap-3 py-1" data-testid="attitude-question">
                          <span aria-hidden="true" className="mt-2.5 h-2 w-2 shrink-0 rounded-full" style={{ backgroundColor: "#C8102E" }} />
                          <span>{line.trim()}</span>
                        </span>
                      );
                    }
                    return (
                      <span key={j} className={/^A\d+\.\s/.test(line) || /^Key assessment:/.test(line) ? "font-bold text-teal" : /^ARMS Ltd Assessment Methodology$/.test(line.trim()) || isBookTitle ? "font-bold text-navy" : undefined}>
                        {cover && <img src={cover.src} alt="" aria-hidden="true" className="mr-2 -mt-1 inline-block h-7 w-auto rounded-[3px] shadow-sm align-middle" />}
                        {line}
                        {"\n"}
                      </span>
                    );
                  })}
                </p>
              );
            })}
          </div>
        </div>
      </article>
      <CtaBand
        title={article.slug === "what-influenced-the-arms-ltd-approach" ? "Do you share our reading interests?" : "Facing the issues in this article?"}
        text={article.slug === "what-influenced-the-arms-ltd-approach" ? "Tell us what is happening in your organisation and challenge us to describe a way to improve things" : article.slug === "how-complacent-attitudes-form-and-why" ? "Tell us what's happening in your organisation — do you worry about the creep of complacency? Talk to us, we can help you understand why it happens and what to do about it" : "Tell us what's happening in your organisation — your culture will have formed gradually but we can help you with rapid transformation"}
      />
    </div>
  );
}
